// client/src/v2/components/drawer/CatchmentPanel.jsx

import React, { useMemo, useState } from "react";
import { formatRadiusDistance } from "../../utils/formatRadiusDistance";
import StepFlow from "./stats/StepFlow";
import InfoTip from "../InfoTip";

function toTitleCase(str) {
  if (!str || str === "—") return str;
  return str
    .split(/\s+|_/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** Parent-friendly labels for catchment category (from schools.catchment_category). */
const CATCHMENT_CATEGORY_DISPLAY = {
  designated: "Priority area",
  open: "Open to applicants from anywhere",
  both: "Priority area and open catchment",
};

function formatCatchmentCategory(raw) {
  if (!raw || !String(raw).trim()) return "—";
  const key = String(raw).trim().toLowerCase();
  return CATCHMENT_CATEGORY_DISPLAY[key] ?? toTitleCase(raw);
}

/** Use DB display column, fallback to technical column (title-cased) when null. */
function formatDisplay(display, technical) {
  if (display && String(display).trim()) return String(display).trim();
  if (technical && String(technical).trim()) return toTitleCase(technical);
  return "—";
}

/** Catchment boundary: use DB display, or for radius show "X unit" when display is null. */
function formatBoundaryDisplay(def) {
  const gt = String(def?.geography_type || "").toLowerCase();
  const rawDisplay = def?.catchment_boundary_display?.trim();
  if (rawDisplay) {
    // Radius rows often store "6.700 km …" — tidy the leading number (max 2 dp, trim zeros).
    if (gt === "radius" && def?.radius != null && /^\d/.test(rawDisplay)) {
      return rawDisplay.replace(/^\d+(?:\.\d+)?/, formatRadiusDistance(def.radius));
    }
    return rawDisplay;
  }
  if (gt === "radius") {
    const r = def?.radius != null ? formatRadiusDistance(def.radius) : "—";
    const u = (def?.radius_unit || "").trim();
    return `${r}${u ? " " + u : ""}`.trim();
  }
  return formatDisplay(null, def?.geography_type);
}

/** Tiffin Girls 2026 arrangements: EHCP, looked-after, Pupil Premium, then the two areas. */
const TIFFIN_GIRLS_CRITERIA = ["EHCP", "Looked-after children", "Pupil Premium"];

/** Wilmington Girls note: EHCP, looked-after, sibling, staff, health, Pupil Premium, then the areas. */
const WILMINGTON_GIRLS_CRITERIA = [
  "EHCP",
  "Looked-after children",
  "Sibling",
  "Children of staff",
  "Health",
  "Pupil Premium",
];

/** Carre's published order. 2027 changes the sibling school to the Community Inclusive Trust. */
const CARRES_PLACE_ORDER = {
  2026: [
    "Looked-after children who met the standard",
    "Pupil Premium",
    "Sibling at a Robert Carre Trust school",
    "Straight-line distance",
  ],
  2027: [
    "Looked-after children who met the standard",
    "Pupil Premium",
    "Sibling at a Community Inclusive Trust school",
    "Straight-line distance",
  ],
};

function isRealCatchmentBand(def) {
  const kind = def?.style?.kind;
  return kind !== "historic_nod" && kind !== "driving_indicative";
}

/**
 * Renders the 11+ Catchment section with year toggles and per-priority definitions.
 * Uses existing v2-detail-row styling for consistency.
 */
export default function CatchmentPanel({ payload, school, loading, error, selectedIds = [], onShowCatchment }) {
  const years = useMemo(() => {
    const defs = payload?.definitions ?? [];
    const policies = payload?.admissionsPolicies ?? [];
    const fromDefs = defs.map((d) => d.catchment_year).filter(Boolean);
    const fromPolicies = policies.map((p) => p.entry_year).filter(Boolean);
    const set = new Set([...fromDefs, ...fromPolicies]);
    return Array.from(set).sort((a, b) => (b || 0) - (a || 0));
  }, [payload?.definitions, payload?.admissionsPolicies]);

  const [selectedYear, setSelectedYear] = useState(null);

  // Default to most recent year when years change
  const effectiveYear = useMemo(() => {
    if (selectedYear && years.includes(selectedYear)) return selectedYear;
    return years[0] ?? null;
  }, [years, selectedYear]);

  const policyForYear = useMemo(() => {
    const policies = payload?.admissionsPolicies ?? [];
    return policies.find((p) => p.entry_year === effectiveYear) ?? null;
  }, [payload?.admissionsPolicies, effectiveYear]);

  const definitionsForYear = useMemo(() => {
    const defs = (payload?.definitions ?? []).filter(
      (d) => d?.style?.show_in_panel !== false
    );
    const forYear = defs.filter((d) => d.catchment_year === effectiveYear);
    if (forYear.length > 0) {
      return forYear.sort((a, b) => (a.catchment_priority ?? 99) - (b.catchment_priority ?? 99));
    }
    const fallbackYear = defs
      .map((d) => d.catchment_year)
      .filter((y) => y != null)
      .sort((a, b) => (b || 0) - (a || 0))[0];
    if (fallbackYear == null) return [];
    return defs
      .filter((d) => d.catchment_year === fallbackYear)
      .sort((a, b) => (a.catchment_priority ?? 99) - (b.catchment_priority ?? 99));
  }, [payload?.definitions, effectiveYear]);

  const schoolFromPayload = payload?.school ?? school;
  const schoolId = school?.id ?? schoolFromPayload?.id;

  /** Same as expand: add catchment if not selected. Only section collapse removes. */
  const handlePanelClick = (e) => {
    if (!onShowCatchment || !schoolId) return;
    if (e.target.closest("a")) return;
    if (!selectedIds.includes(schoolId)) onShowCatchment(schoolId);
  };

  const canShowCatchment = !!onShowCatchment && !!schoolId && payload?.definitions?.length > 0;

  if (loading) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted">Loading catchment data…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted" style={{ color: "#c00" }}>{error}</div>
      </div>
    );
  }

  const hasDefinitions = payload?.definitions?.length > 0;
  const hasPolicies = payload?.admissionsPolicies?.length > 0;

  if (!payload || (!hasDefinitions && !hasPolicies)) {
    const cat = schoolFromPayload?.catchment_category;
    const isOpen = (cat || "").toLowerCase() === "open";
    return (
      <div className="v2-catchment-panel">
        <div className="v2-detail-section v2-catchment-block-first">
          <div className="v2-detail-row">
            <span className="v2-detail-label">Catchment</span>
            <span className="v2-detail-value">{isOpen ? formatCatchmentCategory(cat) : "—"}</span>
          </div>
          {!isOpen ? (
            <div className="v2-muted" style={{ marginTop: 4 }}>No catchment definitions available for this school.</div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className="v2-catchment-panel"
      onClick={canShowCatchment ? handlePanelClick : undefined}
      onKeyDown={canShowCatchment ? (e) => e.key === "Enter" && handlePanelClick(e) : undefined}
      role={canShowCatchment ? "button" : undefined}
      tabIndex={canShowCatchment ? 0 : undefined}
      aria-label={canShowCatchment ? "Show or hide this catchment on the map" : undefined}
      style={canShowCatchment ? { cursor: "pointer" } : undefined}
    >
      {years.length > 0 ? (
        <div className="v2-catchment-year-toggles">
          {years.map((y) => (
            <button
              key={y}
              type="button"
              className={`v2-catchment-year-btn ${y === effectiveYear ? "v2-catchment-year-btn--active" : ""}`}
              onClick={() => setSelectedYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      ) : null}

      <div className="v2-catchment-content">
        <StandardCatchment
          policy={policyForYear}
          bands={definitionsForYear.filter(isRealCatchmentBand)}
          schoolCode={String(school?.school_code || payload?.school?.school_code || "")}
          year={effectiveYear}
        />
      </div>
    </div>
  );
}

function CatchmentFact({ label, children, stack = false }) {
  return (
    <div className={`v2-catchment-fact${stack ? " v2-catchment-fact--stack" : ""}`}>
      <div className="v2-catchment-fact__label">{label}</div>
      <div className="v2-catchment-fact__value">{children}</div>
    </div>
  );
}

function memberEntries(def) {
  const members = Array.isArray(def?.members_display) ? def.members_display : [];
  return members
    .map((m) => {
      if (m && typeof m === "object") {
        return { name: String(m.name || m.code || "").trim(), group: String(m.group || "").trim() };
      }
      return { name: String(m || "").trim(), group: "" };
    })
    .filter((entry) => entry.name);
}

function memberNames(def) {
  return memberEntries(def).map((entry) => entry.name);
}

/** Group headings are used only when a member stores one. Other schools stay a flat list. */
function namedAreaGroups(defs) {
  const entries = (defs || []).flatMap((def) => memberEntries(def));
  if (!entries.some((entry) => entry.group)) return null;
  const groups = [];
  for (const entry of entries) {
    const label = entry.group || "Other";
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.names.push(entry.name);
    else groups.push({ label, names: [entry.name] });
  }
  return groups;
}

function seatsText(def) {
  const display = def?.catchment_alloc_seats_display?.trim();
  if (display && display !== "—") return display;
  if (def?.catchment_alloc_seats != null) return String(def.catchment_alloc_seats);
  return "";
}

function uniqueTexts(values) {
  const seen = new Set();
  const out = [];
  for (const value of values) {
    const text = String(value || "").trim();
    if (!text || seen.has(text)) continue;
    seen.add(text);
    out.push(text);
  }
  return out;
}

/** Same priority is one catchment, even when the map uses a ring and a parish list. */
function groupBands(bands) {
  const groups = [];
  for (const def of bands) {
    const priority = def.catchment_priority ?? 99;
    const last = groups[groups.length - 1];
    if (last && last.priority === priority) last.defs.push(def);
    else groups.push({ priority, defs: [def] });
  }
  return groups;
}

function namedAreaNames(defs) {
  return uniqueTexts((defs || []).flatMap((def) => memberNames(def)));
}

function NamedAreaValue({ defs }) {
  const [open, setOpen] = useState(false);
  const groups = namedAreaGroups(defs);
  const names = namedAreaNames(defs);
  if (groups) {
    return (
      <div className="v2-catchment-groups">
        {groups.map((group) => (
          <div key={group.label} className="v2-catchment-group">
            <div className="v2-catchment-group__title">{group.label}</div>
            <ul className="v2-catchment-group__list">
              {group.names.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  const onlyCircle = defs.every((def) => {
    const geography = String(def?.geography_type || "").toLowerCase();
    return geography === "open" || geography === "radius";
  });
  if (!names.length) return onlyCircle ? "None" : "—";
  const shown = open ? names : names.slice(0, 8);
  const hidden = names.length - shown.length;
  return (
    <span className="v2-catchment-names">
      {shown.map((name) => (
        <span key={name} className="v2-catchment-name">{name}</span>
      ))}
      {names.length > 8 ? (
        <button
          type="button"
          className="v2-catchment-names__more"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((current) => !current);
          }}
        >
          {open ? "Show less" : `and ${hidden} more`}
        </button>
      ) : null}
    </span>
  );
}

/** A note sentence the card already shows as Named area or Place order. */
function necessaryNote(note) {
  const text = String(note || "").trim();
  if (!text) return "";
  const criterion = /looked-after|looked after|pupil premium|sibling|sister|brother|\bstaff\b|ehcp|\blac\b/i;
  const addsFact = /\d|no fixed|within|preferential|not drawn|not used|not a |tie-break|qualifying|quota|walking|parish|ward|feeder|pastoral|transport|circle|ring\b|mile|score|standard|up to|about |postcode|borough|trafford|manchester|zone|gis|cap\b/i;
  const kept = text.split(/(?<=[.!?])\s+/).flatMap((raw) => {
    let sentence = raw.trim().replace(
      /\s*\((?:after|and)\s+[^)]*(?:lac|looked-after|looked after|sibling|staff|pupil premium|ehcp)[^)]*\)/gi,
      ""
    );
    sentence = sentence.replace(/\s{2,}/g, " ").trim();
    if (!sentence) return [];
    const postcodes = sentence.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\b/g) || [];
    if (/^the map (is|shows)\b/i.test(sentence) && postcodes.length >= 3) return [];
    if (criterion.test(sentence) && !addsFact.test(sentence)) return [];
    return [sentence];
  });
  return kept.join(" ");
}

function bandNotes(defs) {
  return uniqueTexts(defs.map((def) => necessaryNote(def?.style?.panel_note))).filter(Boolean).join("\n\n");
}

/** KCC / NOD not-full status stored on distance-first pending defs. */
function nodStatusInfo(defs) {
  for (const def of defs || []) {
    const status = String(def?.style?.nod_status || "").trim().toLowerCase();
    if (status !== "not_full" && status !== "partial_not_full") continue;
    const detail = String(def?.style?.nod_status_label || "").trim();
    return {
      status,
      title: status === "partial_not_full" ? "Mostly not full" : "Not full",
      detail:
        detail ||
        "National Offer Day was not full — no distance cut-off ring is drawn on the map.",
    };
  }
  return null;
}

function nodBannerDetail(info) {
  let detail = String(info?.detail || "").trim();
  // Badge already says Not full — drop the repeated lead-in.
  detail = detail
    .replace(/^National Offer Day:\s*(mostly\s+)?not full\s*[—–-]?\s*/i, "")
    .replace(/^National Offer Day:\s*/i, "")
    .trim();
  if (!detail) {
    return "Recent Offer Days had places left — so no distance cut-off ring is drawn on the map.";
  }
  // Capitalise if we stripped a mid-sentence fragment.
  return detail.charAt(0).toUpperCase() + detail.slice(1);
}

function NodStatusBanner({ info }) {
  if (!info) return null;
  return (
    <aside className="v2-catchment-nod" aria-label="National Offer Day status">
      <div className="v2-catchment-nod__row">
        <span className="v2-catchment-nod__badge">{info.title}</span>
        <span className="v2-catchment-nod__kicker">National Offer Day</span>
      </div>
      <p className="v2-catchment-nod__detail">{nodBannerDetail(info)}</p>
    </aside>
  );
}

/** A radius or a named area is a catchment. Open geography alone is not. */
function schoolHasFixedArea(bands) {
  return bands.some((def) => String(def?.geography_type || "").toLowerCase() !== "open");
}

/**
 * Same two blocks for every school.
 * Meta is the year facts. Each real catchment is one box, with the same rows.
 */
function StandardCatchment({ policy, bands, schoolCode, year }) {
  const policyHref = policy?.policy_url
    ? (policy.policy_url.startsWith("http") ? policy.policy_url : `https://${policy.policy_url}`)
    : null;
  const groups = groupBands(bands);
  const nodInfo = nodStatusInfo(bands);

  return (
    <div className="v2-catchment-standard">
      <NodStatusBanner info={nodInfo} />
      <div className="ds-tiles v2-catchment-meta">
        <div className="ds-tile ds-pan">
          <span className="ds-tile-label">
            PAN
            <InfoTip text="Published admission number: Year 7 places in this school’s admission policy for the selected year." />
          </span>
          <strong>{policy?.total_intake ?? "—"}</strong>
        </div>
        <div className="ds-tile">
          <span>Admission policy</span>
          <strong>
            {policyHref ? (
              <a
                className="v2-drawer-link"
                href={policyHref}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                View admission policy →
              </a>
            ) : (
              "—"
            )}
          </strong>
        </div>
      </div>

      {bands.length === 0 ? (
        <section className="v2-catchment-box v2-catchment-box--empty">
          No catchment area published for this year.
        </section>
      ) : !schoolHasFixedArea(bands) ? (
        <NoCatchmentBox bands={bands} schoolCode={schoolCode} year={year} nodInfo={nodInfo} />
      ) : (
        <>
          {groups.map((group, index) => (
            <CatchmentBandBox key={group.priority} group={group} schoolCode={schoolCode} index={index} />
          ))}
          <PlaceOrderBox schoolCode={schoolCode} year={year} groups={groups} defs={bands} />
        </>
      )}
    </div>
  );
}

function bandTitle(group) {
  return uniqueTexts(group.defs.map((def) => formatDisplay(def.catchment_type_display, def.catchment_key))).join(" and ");
}

function catchmentCardTitle(group, index) {
  const name = bandTitle(group);
  return name && name !== "—" ? `Catchment ${index + 1}: ${name}` : `Catchment ${index + 1}`;
}

/** Score bands use "Marks first". Distance bands use "Rank order". */
function shortRanking(priority) {
  const raw = String(priority || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("exam") || raw.includes("score") || raw.includes("mark")) return "Marks first";
  if (raw.includes("distance") || raw.includes("rank")) return "Rank order";
  return "";
}

function rankingText(defs, schoolCode) {
  if (schoolCode === "136615") return "Marks first";
  if (schoolCode === "137213") return "Rank order";
  return uniqueTexts(defs.map((def) => shortRanking(def.admission_priority))).join(" · ") || "—";
}

function storedPlaceOrder(defs) {
  for (const def of defs || []) {
    const steps = def?.style?.place_order;
    if (Array.isArray(steps) && steps.length) {
      return steps.map((step) => String(step).trim()).filter(Boolean);
    }
  }
  return null;
}

function placeOrderSteps(schoolCode, year, groups, defs) {
  const catchmentSteps = groups.map((group, index) => catchmentCardTitle(group, index));
  const stored = storedPlaceOrder(defs);
  if (stored) {
    const alreadyNamed = stored.some((step) => /^Catchment \d+/.test(step));
    const orderAlreadyComplete = (defs || []).some((def) => def?.style?.place_order_complete === true);
    if (orderAlreadyComplete || alreadyNamed || !catchmentSteps.length) return stored;
    return [...stored, ...catchmentSteps];
  }
  if (schoolCode === "137213") return CARRES_PLACE_ORDER[year] || CARRES_PLACE_ORDER[2027];
  if (schoolCode === "136615") return [...TIFFIN_GIRLS_CRITERIA, ...catchmentSteps];
  if (schoolCode === "137250") return [...WILMINGTON_GIRLS_CRITERIA, ...catchmentSteps];
  if (schoolCode === "118843") return ["EHCP", ...catchmentSteps];
  return catchmentSteps.filter((title) => title && title !== "—");
}

function CatchmentBandBox({ group, schoolCode, index }) {
  const notes = bandNotes(group.defs);
  const areaNames = namedAreaNames(group.defs);
  return (
    <section className="v2-catchment-box">
      <h4 className="v2-catchment-box__title">{catchmentCardTitle(group, index)}</h4>
      <div className="v2-catchment-scroll">
        <CatchmentFact label="Boundary">
          {uniqueTexts(group.defs.map((def) => formatBoundaryDisplay(def))).join(" · ") || "—"}
        </CatchmentFact>
        <CatchmentFact label="Named area" stack={areaNames.length > 0}><NamedAreaValue defs={group.defs} /></CatchmentFact>
        <CatchmentFact label="Seats">{uniqueTexts(group.defs.map(seatsText)).join(" · ") || "—"}</CatchmentFact>
        <CatchmentFact label="Ranking">
          {rankingText(group.defs, schoolCode)}
        </CatchmentFact>
        {notes ? <p className="v2-catchment-note">{notes}</p> : null}
      </div>
    </section>
  );
}

function PlaceOrderBox({ schoolCode, year, groups, defs }) {
  const steps = placeOrderSteps(schoolCode, year, groups, defs);
  return (
    <section className="v2-catchment-box">
      <h4 className="v2-catchment-box__title">Place order</h4>
      <div className="v2-catchment-scroll">
        {steps.length ? <StepFlow steps={steps} label="Place order" /> : "—"}
      </div>
    </section>
  );
}

/** Same rows as a catchment card. The title says there is no area. */
function NoCatchmentBox({ bands, schoolCode, year, nodInfo }) {
  const notes = bandNotes(bands);
  const areaNames = namedAreaNames(bands);
  const distanceFirst = bands.some(
    (def) => String(def?.catchment_key || "").toLowerCase() === "distance_ranked"
  );
  const title = distanceFirst
    ? nodInfo
      ? `Distance-ranked · ${nodInfo.title}`
      : "Distance-ranked (no drawn ring)"
    : "No catchment";
  return (
    <>
      <section className="v2-catchment-box">
        <h4 className="v2-catchment-box__title">{title}</h4>
        <div className="v2-catchment-scroll">
          <CatchmentFact label="Boundary">
            {uniqueTexts(bands.map((def) => formatBoundaryDisplay(def))).join(" · ") || "—"}
          </CatchmentFact>
          <CatchmentFact label="Named area" stack={areaNames.length > 0}><NamedAreaValue defs={bands} /></CatchmentFact>
          <CatchmentFact label="Seats">{uniqueTexts(bands.map(seatsText)).join(" · ") || "—"}</CatchmentFact>
          <CatchmentFact label="Ranking">{rankingText(bands, schoolCode)}</CatchmentFact>
          {notes ? <p className="v2-catchment-note">{notes}</p> : null}
        </div>
      </section>
      <PlaceOrderBox schoolCode={schoolCode} year={year} groups={[]} defs={bands} />
    </>
  );
}
